/**
 * DataStudio - SQL query builder and executor for custom dashboards
 *
 * Features:
 * - SQL query text area with syntax highlighting
 * - Autocomplete for entity names, fields, and SQL keywords
 * - Query execution with result preview
 * - Add result as tile to dashboard
 */

import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@getdevpulse/shared-ui"
import { X } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useCallback, useRef, useState } from "react"
import { executeQuery, getAutocompleteSuggestions, parseQuery } from "../lib/sqlQuery"
import type { VisualizationType } from "../stores/CustomDashboardStore"
import { useCustomDashboardStore, useDataStore } from "../stores/StoreContext"

interface DataStudioProps {
  onClose?: () => void
}

export const DataStudio = observer(({ onClose }: DataStudioProps) => {
  const dataStore = useDataStore()
  const customDashboardStore = useCustomDashboardStore()

  const [query, setQuery] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0)
  const [cursorPosition, setCursorPosition] = useState(0)
  const [tileName, setTileName] = useState("")
  const [visualizationType, setVisualizationType] = useState<VisualizationType>("table")
  const [queryResult, setQueryResult] = useState<{
    columns: string[]
    rows: Record<string, unknown>[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)

  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  // Example queries for quick start
  const exampleQueries = [
    {
      label: "Top Contributors by Commits",
      query:
        "SELECT authorHandle, COUNT(*) AS commit_count FROM commits GROUP BY authorHandle ORDER BY commit_count DESC LIMIT 10",
    },
    {
      label: "PRs Merged Last Month",
      query:
        "SELECT repoName, COUNT(*) AS pr_count FROM prs WHERE state = 'merged' GROUP BY repoName ORDER BY pr_count DESC",
    },
    {
      label: "Open Issues by Repository",
      query:
        "SELECT repoName, COUNT(*) AS issue_count FROM issues WHERE state = 'open' GROUP BY repoName ORDER BY issue_count DESC",
    },
    {
      label: "Recent High Complexity Commits",
      query:
        "SELECT authorHandle, message, score FROM commits WHERE complexityClass = 'very-high' ORDER BY timestamp DESC LIMIT 20",
    },
  ]

  // Update autocomplete suggestions when cursor position changes
  const updateSuggestions = useCallback((text: string, cursor: number) => {
    const newSuggestions = getAutocompleteSuggestions(text, cursor)
    setSuggestions(newSuggestions)
    setShowSuggestions(newSuggestions.length > 0)
    setSelectedSuggestionIndex(0)
  }, [])

  // Handle text area changes
  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newQuery = e.target.value
    const newCursor = e.target.selectionStart
    setQuery(newQuery)
    setCursorPosition(newCursor)
    updateSuggestions(newQuery, newCursor)
  }

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedSuggestionIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedSuggestionIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === "Tab" || e.key === "Enter") {
      if (showSuggestions) {
        e.preventDefault()
        applySuggestion(suggestions[selectedSuggestionIndex])
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  // Apply selected suggestion
  const applySuggestion = (suggestion: string) => {
    const beforeCursor = query.substring(0, cursorPosition)
    const afterCursor = query.substring(cursorPosition)
    const lastWord = beforeCursor.split(/\s+/).pop() || ""
    const beforeLastWord = beforeCursor.substring(0, beforeCursor.length - lastWord.length)

    const newQuery = beforeLastWord + suggestion + afterCursor
    setQuery(newQuery)
    setShowSuggestions(false)

    // Move cursor after suggestion
    setTimeout(() => {
      if (textAreaRef.current) {
        const newCursorPos = beforeLastWord.length + suggestion.length
        textAreaRef.current.setSelectionRange(newCursorPos, newCursorPos)
        textAreaRef.current.focus()
      }
    }, 0)
  }

  // Execute query
  const handleExecuteQuery = useCallback(() => {
    if (!query.trim()) return

    setIsExecuting(true)
    setError(null)
    setQueryResult(null)

    try {
      const parsedQuery = parseQuery(query)

      // Get the appropriate data based on the entity
      let entityData: Record<string, unknown>[] = []
      switch (parsedQuery.from) {
        case "commits":
          entityData = dataStore.allCommits as unknown as Record<string, unknown>[]
          break
        case "prs":
          entityData = dataStore.allPullRequests as unknown as Record<string, unknown>[]
          break
        case "issues":
          entityData = dataStore.allIssues as unknown as Record<string, unknown>[]
          break
        case "releases":
          entityData = dataStore.allReleases as unknown as Record<string, unknown>[]
          break
      }

      const result = executeQuery(parsedQuery, entityData)
      setQueryResult({ columns: result.columns, rows: result.rows })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Query execution failed")
    } finally {
      setIsExecuting(false)
    }
  }, [query, dataStore])

  // Add result as tile
  const handleAddTile = useCallback(() => {
    if (!queryResult || !tileName.trim()) return

    const tileId = customDashboardStore.addTile(tileName, query, visualizationType)
    if (tileId) {
      // Execute the tile query to populate initial results
      const parsedQuery = parseQuery(query)

      let entityData: Record<string, unknown>[] = []
      switch (parsedQuery.from) {
        case "commits":
          entityData = dataStore.allCommits as unknown as Record<string, unknown>[]
          break
        case "prs":
          entityData = dataStore.allPullRequests as unknown as Record<string, unknown>[]
          break
        case "issues":
          entityData = dataStore.allIssues as unknown as Record<string, unknown>[]
          break
        case "releases":
          entityData = dataStore.allReleases as unknown as Record<string, unknown>[]
          break
      }

      customDashboardStore.executeTileQuery(tileId, entityData)

      // Reset form and close
      setQuery("")
      setTileName("")
      setQueryResult(null)
      onClose?.()
    }
  }, [queryResult, tileName, query, visualizationType, customDashboardStore, dataStore, onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Data Studio</h2>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Example Queries */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-2">Example Queries:</h3>
            <div className="flex flex-wrap gap-2">
              {exampleQueries.map((example) => (
                <Badge
                  key={example.label}
                  variant="secondary"
                  className="cursor-pointer hover:bg-secondary/80"
                  onClick={() => setQuery(example.query)}
                >
                  {example.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Query Editor */}
          <div className="mb-4">
            <label htmlFor="query" className="block text-sm font-medium mb-2">
              SQL Query
            </label>
            <div className="relative">
              <textarea
                ref={textAreaRef}
                id="query"
                value={query}
                onChange={handleQueryChange}
                onKeyDown={handleKeyDown}
                className="w-full h-32 px-3 py-2 font-mono text-sm border rounded-md bg-background focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                placeholder="SELECT * FROM commits WHERE ..."
              />

              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-accent ${
                        index === selectedSuggestionIndex ? "bg-accent" : ""
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Execute Button */}
          <div className="mb-6">
            <Button onClick={handleExecuteQuery} disabled={!query.trim() || isExecuting}>
              {isExecuting ? "Executing..." : "Execute Query"}
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Query Results */}
          {queryResult && (
            <div>
              <h3 className="text-lg font-medium mb-4">
                Query Results ({queryResult.rows.length} rows)
              </h3>

              <div className="overflow-x-auto mb-4 border rounded-md">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-muted">
                      {queryResult.columns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider border-b"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queryResult.rows.slice(0, 100).map((row, idx) => (
                      <tr
                        key={`${idx}-${JSON.stringify(row)}`}
                        className={idx % 2 === 0 ? "bg-background" : "bg-muted/50"}
                      >
                        {queryResult.columns.map((col) => (
                          <td key={col} className="px-4 py-2 text-sm border-b">
                            {String(row[col] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add as Tile Form */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-md font-medium mb-3">Add to Dashboard</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="tileName" className="block text-sm font-medium mb-1">
                      Tile Name
                    </label>
                    <Input
                      type="text"
                      id="tileName"
                      value={tileName}
                      onChange={(e) => setTileName(e.target.value)}
                      placeholder="My Custom Report"
                    />
                  </div>

                  <div>
                    <label htmlFor="visualizationType" className="block text-sm font-medium mb-1">
                      Visualization Type
                    </label>
                    <Select
                      value={visualizationType}
                      onValueChange={(value) => setVisualizationType(value as VisualizationType)}
                    >
                      <SelectTrigger id="visualizationType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="table">Table</SelectItem>
                        <SelectItem value="bar">Bar Chart</SelectItem>
                        <SelectItem value="line">Line Chart</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button onClick={handleAddTile} disabled={!tileName.trim()} variant="default">
                  Add Tile to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
